// Global Instructions Rule Applied!
import { ProspectFields } from './fieldConfigs.ts'
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'
import { ProspectRecordFields } from '../../../state_manager/data_models/prospect_record.ts'
import { FIELD_KEY_LABEL } from '../../../state_manager/globals.ts'
import type { FormInstance } from 'antd'

export interface BusinessRuleResult {
  formData: Record<string, any>
  formUpdates?: Record<string, any>
  helperFieldUpdates?: Record<string, any>
}

/**
 * Handle new investor toggle change
 */
function handleNewInvestorToggle(
  field: string,
  value: boolean,
  currentFormData: Record<string, any>,
  form: FormInstance
): BusinessRuleResult | null {
  if (field !== ProspectFields.prog__is_new_investor) return null

  const newData = { ...currentFormData, [field]: value }
  const formUpdates: Record<string, any> = {}
  const helperFieldUpdates: Record<string, any> = {}

  if (value === true) {
    // When new investor = true, clear selected investor field
    newData[ProspectFields.prospects__investor] = ''
    formUpdates[ProspectFields.prospects__investor] = ''
    // Note: Unlike deals, we don't auto-populate investor name from prospect name (per spec)
  } else {
    // When new investor = false, clear investor field and new investor fields
    newData[ProspectFields.prospects__investor] = ''
    formUpdates[ProspectFields.prospects__investor] = ''

    // Clear new investor fields
    newData[AccountRecordFields.accounts__name] = ''
    newData[AccountRecordFields.accounts__industry] = ''
    newData[AccountRecordFields.accounts__website] = ''
    newData[AccountRecordFields.accounts__business_city_code] = ''
    newData[AccountRecordFields.accounts__business_country_code] = ''
    newData[AccountRecordFields.accounts__business_stateprovince] = ''
    newData[AccountRecordFields.accounts__description] = ''

    formUpdates[AccountRecordFields.accounts__name] = ''
    formUpdates[AccountRecordFields.accounts__industry] = ''
    formUpdates[AccountRecordFields.accounts__website] = ''
    formUpdates[AccountRecordFields.accounts__business_city_code] = ''
    formUpdates[AccountRecordFields.accounts__business_country_code] = ''
    formUpdates[AccountRecordFields.accounts__business_stateprovince] = ''
    formUpdates[AccountRecordFields.accounts__description] = ''

    helperFieldUpdates.investor_city_name = ''
  }

  return {
    formData: newData,
    formUpdates: Object.keys(formUpdates).length > 0 ? formUpdates : undefined,
    helperFieldUpdates: Object.keys(helperFieldUpdates).length > 0 ? helperFieldUpdates : undefined
  }
}

/**
 * Handle investor change
 */
function handleInvestorChange(
  field: string,
  value: any,
  currentFormData: Record<string, any>
): BusinessRuleResult | null {
  if (field !== ProspectFields.prospects__investor) return null

  return {
    formData: { ...currentFormData, [field]: value }
  }
}

/**
 * Handle city change - auto-populate country when city is selected
 */
function handleCityChange(field: string, value: any, currentFormData: Record<string, any>): BusinessRuleResult | null {
  if (
    field !== AccountRecordFields.accounts__business_city_code &&
    field !== ProspectRecordFields.accounts__business_city_code__label
  )
    return null

  const newData = { ...currentFormData, [field]: value }
  const formUpdates: Record<string, any> = {}
  const helperFieldUpdates: Record<string, any> = {}

  const countryCode = value?.data?.countryCode || value?.countryCode
  const cityLabel = value?.name || value?.label || ''
  const state = value?.data?.state ?? null
  const cityCode = typeof value === 'object' ? value?.value || value?.code : value

  // InputManager reads from formData (via fields prop), so we need the full object there
  // CitySelect component expects object format {value: code, name: label, data: {...}}
  if (cityCode !== undefined && cityCode !== null && cityCode !== '') {
    // Preserve the full city object for formData (InputManager reads from here)
    if (typeof value === 'object' && value !== null) {
      newData[AccountRecordFields.accounts__business_city_code] = value
      formUpdates[AccountRecordFields.accounts__business_city_code] = value
    } else {
      // If value is not an object, create one from the code
      const cityObject = {
        value: cityCode,
        name: cityLabel || `City ${cityCode}`,
        label: cityLabel || `City ${cityCode}`,
        data: { countryCode }
      }
      newData[AccountRecordFields.accounts__business_city_code] = cityObject
      formUpdates[AccountRecordFields.accounts__business_city_code] = cityObject
    }
  }

  newData[AccountRecordFields.accounts__business_stateprovince] = state || null
  formUpdates[AccountRecordFields.accounts__business_stateprovince] = state || null

  if (countryCode) {
    newData[AccountRecordFields.accounts__business_country_code] = countryCode
    formUpdates[AccountRecordFields.accounts__business_country_code] = countryCode

    if (cityLabel) {
      const cityLabelField = `${AccountRecordFields.accounts__business_city_code}${FIELD_KEY_LABEL}`
      newData[cityLabelField] = cityLabel
      formUpdates[cityLabelField] = cityLabel
    }

    helperFieldUpdates.investor_city_name = cityLabel
  }

  return {
    formData: newData,
    formUpdates: Object.keys(formUpdates).length > 0 ? formUpdates : undefined,
    helperFieldUpdates: Object.keys(helperFieldUpdates).length > 0 ? helperFieldUpdates : undefined
  }
}

/**
 * Handle country change - clear city when country changes
 */
function handleCountryChange(
  field: string,
  value: any,
  currentFormData: Record<string, any>
): BusinessRuleResult | null {
  if (
    field !== AccountRecordFields.accounts__business_country_code &&
    field !== ProspectRecordFields.accounts__business_country_code__label
  )
    return null

  const newData = { ...currentFormData, [field]: value }
  const formUpdates: Record<string, any> = {}

  const countryCode = value?.value || value
  const countryLabel = value?.label || value

  // Clear city and state when country changes
  const cityLabelField = `${AccountRecordFields.accounts__business_city_code}${FIELD_KEY_LABEL}`
  newData[AccountRecordFields.accounts__business_city_code] = null
  newData[cityLabelField] = null
  newData[AccountRecordFields.accounts__business_stateprovince] = null
  formUpdates[AccountRecordFields.accounts__business_city_code] = null
  formUpdates[cityLabelField] = null
  formUpdates[AccountRecordFields.accounts__business_stateprovince] = null

  // Set country code and label
  if (countryCode) {
    newData[AccountRecordFields.accounts__business_country_code] = countryCode
    formUpdates[AccountRecordFields.accounts__business_country_code] = countryCode
  }

  if (countryLabel && countryLabel !== countryCode) {
    newData[ProspectRecordFields.accounts__business_country_code__label] = countryLabel
    formUpdates[ProspectRecordFields.accounts__business_country_code__label] = countryLabel
  }

  return {
    formData: newData,
    formUpdates: Object.keys(formUpdates).length > 0 ? formUpdates : undefined
  }
}

/**
 * Main handler for field changes - applies all business rules
 */
export function handleFieldChange(
  field: string,
  value: any,
  currentFormData: Record<string, any>,
  form: FormInstance
): BusinessRuleResult {
  let newData = { ...currentFormData, [field]: value }

  // Apply business rules in order
  const newInvestorResult = handleNewInvestorToggle(field, value, newData, form)
  if (newInvestorResult) {
    newData = newInvestorResult.formData
    if (newInvestorResult.formUpdates) {
      form.setFieldsValue(newInvestorResult.formUpdates)
    }
    return newInvestorResult
  }

  const investorResult = handleInvestorChange(field, value, newData)
  if (investorResult) {
    newData = investorResult.formData
  }

  const cityResult = handleCityChange(field, value, newData)
  if (cityResult) {
    newData = cityResult.formData
    if (cityResult.formUpdates) {
      form.setFieldsValue(cityResult.formUpdates)
    }
    return cityResult
  }

  const countryResult = handleCountryChange(field, value, newData)
  if (countryResult) {
    newData = countryResult.formData
    if (countryResult.formUpdates) {
      form.setFieldsValue(countryResult.formUpdates)
    }
    return countryResult
  }

  // Default: just update the field
  return {
    formData: newData
  }
}
